import { IconButton } from '@material-ui/core';
import { ChangeEvent, FC, useMemo } from 'react';

import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { TransactionCapitalIncentiveItem } from '@shared/apis/transactionsLettingsApi.types';
import { NumberField } from '@shared/components/number-field';
import { TextField } from '@shared/components/text-field';
import { useAgencyConstants } from '@shared/contexts/ConfigContext';
import { t } from '@shared/i18n';
import { CloseIcon } from '@shared/icons/close';
import { InputSize } from '@shared/types/common/input';
import { getCurrencySymbol } from '@shared/utils/number';

import { styles } from './DisposalCapitalIncentiveCard.styles';

export interface DisposalCapitalIncentiveCardProps extends WithStyles<typeof styles> {
  currency: string;
  index: number;
  item: TransactionCapitalIncentiveItem;
  onUpdate: (index: number, updates: Partial<TransactionCapitalIncentiveItem>) => void;
  onDelete: (index: number) => void;
}

const DisposalCapitalIncentiveCardComponent: FC<DisposalCapitalIncentiveCardProps> = ({
  classes,
  currency,
  index,
  item,
  onUpdate,
  onDelete,
}) => {
  const { constants } = useAgencyConstants();

  const currencyAdornment = useMemo(
    () => <span className={classes.currencyAdornment}>{getCurrencySymbol(constants, currency)}</span>,
    [classes.currencyAdornment, constants, currency]
  );

  return (
    <div className={classes.root}>
      <div className={classes.cardHeader}>
        <IconButton onClick={() => onDelete(index)} className={classes.deleteButton}>
          <CloseIcon />
        </IconButton>
      </div>
      <div className={classes.grid}>
        <div className={classes.field}>
          <span className={classes.label}>{t('incentives')}</span>
          <NumberField
            fullWidth
            InputProps={{ startAdornment: currencyAdornment }}
            min={0}
            size={InputSize.medium}
            value={item.amount ?? ''}
            onChange={(value) => onUpdate(index, { amount: Number(value) })}
          />
        </div>
        <div className={classes.field}>
          <span className={classes.label}>{t('comments')}</span>
          <TextField
            classes={{ root: classes.eventTextField }}
            multiline
            fullWidth
            minRows={1}
            placeholder={t('enter_reason_or_comment')}
            value={item.comments ?? ''}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onUpdate(index, { comments: e.target.value })}
          />
        </div>
      </div>
    </div>
  );
};

export const DisposalCapitalIncentiveCard = withStyles(styles)(DisposalCapitalIncentiveCardComponent);
